import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { companyService, Company } from '../services/companyService';
import { dealService, Deal, DealKind, DealStatus } from '../services/dealService';
import { usePermissions } from '../hooks/usePermissions';
import { Permission } from '../utils/permissions';
import { dealAmount } from '../utils/dealsBilling';
import { getAssetUrl } from '../utils/assets';
import './Deals.css';

const ALL_COMPANIES = 'all';

const emptyForm = () => ({
  company: '',
  name: '',
  notes: '',
  kind: 'RECURRING' as DealKind,
  quantity: 1,
  unitPrice: 0,
  support: false,
  overageRate: 0,
  autoInvoice: true,
  status: 'ACTIVE' as DealStatus,
  clientSignature: ''
});

const kindLabel = (kind: DealKind) => (kind === 'ONE_TIME' ? 'One-time' : 'Monthly');
const statusLabel = (status: DealStatus) =>
  status === 'PAUSED' ? 'Paused' : status === 'ENDED' ? 'Ended' : 'Active';
const formatHours = (value: number) => `${Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}h`;
const formatMoney = (value: number) =>
  Number(value || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const companyIdOf = (deal: Deal) =>
  typeof deal.company === 'string' ? deal.company : deal.company?._id || '';

const companyNameOf = (deal: Deal) =>
  typeof deal.company === 'string' ? '' : deal.company?.name || '';

const Deals: React.FC = () => {
  const { can } = usePermissions();
  const canManage = can(Permission.MANAGE_BILLING);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [companyId, setCompanyId] = useState(ALL_COMPANIES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Deal | null>(null);
  const [form, setForm] = useState(emptyForm());

  const clientCompanies = companies.filter((company) => !company.isOwner && company.type !== 'SERVICE_PROVIDER');
  const companyOptions = [
    { label: 'All companies', value: ALL_COMPANIES },
    ...clientCompanies.map((company) => ({ label: company.name, value: company._id }))
  ];
  const formCompanyOptions = clientCompanies.map((company) => ({ label: company.name, value: company._id }));
  const selectedFormCompany = clientCompanies.find((company) => company._id === form.company);
  const companyRate = selectedFormCompany?.billing?.hourlyRate || 0;

  const loadDeals = async (selectedCompany = companyId) => {
    try {
      setLoading(true);
      setError('');
      const data = await dealService.list(selectedCompany === ALL_COMPANIES ? undefined : selectedCompany);
      setDeals(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not load deals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canManage) {
      return;
    }
    companyService.getCompanies()
      .then(setCompanies)
      .catch(() => setCompanies([]));
  }, [canManage]);

  useEffect(() => {
    loadDeals(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const visible = useMemo(() => deals, [deals]);

  const rateFromCompany = (id: string) =>
    clientCompanies.find((company) => company._id === id)?.billing?.hourlyRate || 0;

  const openCreate = () => {
    const nextCompany = companyId !== ALL_COMPANIES ? companyId : clientCompanies[0]?._id || '';
    setEditing(null);
    setForm({
      ...emptyForm(),
      company: nextCompany,
      overageRate: rateFromCompany(nextCompany)
    });
    setShowForm(true);
  };

  const openEdit = (deal: Deal) => {
    setEditing(deal);
    setForm({
      company: companyIdOf(deal),
      name: deal.name,
      notes: deal.notes || '',
      kind: deal.kind,
      quantity: deal.quantity,
      unitPrice: deal.unitPrice,
      support: !!deal.support,
      overageRate: deal.overageRate || rateFromCompany(companyIdOf(deal)),
      autoInvoice: deal.autoInvoice !== false,
      status: deal.status,
      clientSignature: deal.clientSignature || ''
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setSaving(false);
  };

  const saveDeal = async () => {
    if (!form.company || !form.name.trim()) {
      setError('Company and name are required');
      return;
    }
    if (form.support && !(form.overageRate > 0)) {
      setError('Set the hourly rate charged after the package hours are used');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const payload = {
        company: form.company,
        name: form.name.trim(),
        notes: form.notes.trim(),
        kind: form.kind,
        quantity: form.quantity,
        unitPrice: form.unitPrice,
        support: form.support,
        overageRate: form.support ? form.overageRate : 0,
        autoInvoice: form.autoInvoice,
        status: form.status,
        clientSignature: form.clientSignature.startsWith('data:') || form.clientSignature === ''
          ? form.clientSignature
          : undefined
      };
      if (editing) {
        await dealService.update(editing._id, payload);
      } else {
        await dealService.create(payload);
      }
      closeForm();
      await loadDeals();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not save deal');
      setSaving(false);
    }
  };

  const removeDeal = async (deal: Deal) => {
    if (!window.confirm(`Delete deal “${deal.name}”? Existing invoices keep the line item.`)) {
      return;
    }
    try {
      await dealService.remove(deal._id);
      await loadDeals();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not delete deal');
    }
  };

  return (
    <div className="deals-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Deals</h1>
          <p className="page-subtitle">
            {canManage
              ? 'Retainers and packages tied to a company. A support deal uses ticket hours against the package first; extra time is billed at the overage rate. Without a support deal, From hours uses the company hourly rate.'
              : 'Retainers, maintenance, and agreed packages for your company.'}
          </p>
        </div>
        {canManage && (
          <Button label="New deal" icon="pi pi-plus" className="new-ticket-btn" onClick={openCreate} />
        )}
      </div>

      {canManage && (
        <div className="deals-toolbar">
          <Dropdown
            value={companyId}
            options={companyOptions}
            onChange={(e) => setCompanyId(e.value)}
            placeholder="All companies"
            className="w-full md:w-20rem"
            appendTo={document.body}
            panelClassName="deals-dropdown-panel"
          />
        </div>
      )}

      {error && <p className="usage-error">{error}</p>}
      {loading && <p>Loading deals…</p>}

      {!loading && visible.length === 0 && (
        <div className="mobile-empty-state">
          <h3>No deals yet</h3>
          <p>
            {canManage
              ? 'Create a monthly retainer, maintenance plan, or a one-time package for a client company.'
              : 'No retainers or packages have been set up for your company yet.'}
          </p>
        </div>
      )}

      {!loading && visible.length > 0 && (
        <div className="deals-grid">
          {visible.map((deal) => {
            const usage = deal.usage;
            const usedPct = usage && usage.includedHours > 0
              ? Math.min(100, Math.round((usage.usedHours / usage.includedHours) * 100))
              : 0;
            return (
              <div key={deal._id} className={`deal-card status-${deal.status.toLowerCase()}`}>
                <div className="deal-card-head">
                  <div>
                    <h3>{deal.name}</h3>
                    {canManage && (
                      <span className="deal-company">{companyNameOf(deal) || 'Company'}</span>
                    )}
                  </div>
                  <span className={`deal-status deal-status-${deal.status.toLowerCase()}`}>
                    {statusLabel(deal.status)}
                  </span>
                </div>
                {deal.notes && <p className="deal-notes">{deal.notes}</p>}
                <div className="deal-meta">
                  <span className="deal-chip">{kindLabel(deal.kind)}</span>
                  {deal.support && <span className="deal-chip is-support">Support</span>}
                  {deal.autoInvoice && <span className="deal-chip is-auto">Auto-invoice</span>}
                  <span className="deal-chip">
                    {deal.quantity === 1
                      ? formatMoney(deal.unitPrice)
                      : `${deal.quantity} × ${formatMoney(deal.unitPrice)}`}
                  </span>
                </div>
                {deal.support && usage && (
                  <div className={`deal-usage${usage.overageHours > 0 ? ' is-over' : ''}`}>
                    <div className="deal-usage-copy">
                      <strong>
                        {formatHours(usage.usedHours)} / {formatHours(usage.includedHours)}
                      </strong>
                      <span>{usage.periodLabel}</span>
                    </div>
                    <div className="deal-usage-bar" aria-hidden="true">
                      <span style={{ width: `${usedPct}%` }} />
                    </div>
                    <p>
                      {usage.overageHours > 0
                        ? `${formatHours(usage.overageHours)} over the package at ${formatMoney(usage.overageRate)}/h`
                        : `${formatHours(usage.remainingHours)} left in the package`}
                    </p>
                  </div>
                )}
                <div className="deal-amount">
                  {formatMoney(dealAmount(deal))}
                </div>
                <div className="deal-actions">
                  <Button
                    type="button"
                    icon="pi pi-download"
                    className="p-button-secondary p-button-sm p-button-rounded compact-action-btn"
                    onClick={() => window.open(`/deal/${deal._id}`, '_blank')}
                    title="Download PDF"
                  />
                  {canManage && (
                    <>
                      <Button
                        type="button"
                        icon="pi pi-pencil"
                        className="p-button-secondary p-button-sm p-button-rounded compact-action-btn"
                        onClick={() => openEdit(deal)}
                        title="Edit"
                      />
                      <Button
                        type="button"
                        icon="pi pi-trash"
                        className="p-button-danger p-button-sm p-button-rounded compact-action-btn"
                        onClick={() => removeDeal(deal)}
                        title="Delete"
                      />
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {canManage && showForm && (
        <div
          className="app-modal-overlay"
          onClick={(e) => {
            const target = e.target as HTMLElement;
            if (target.closest('.p-dropdown-panel, .p-datepicker')) return;
            closeForm();
          }}
        >
          <div className="app-modal deal-form-card" onClick={(e) => e.stopPropagation()}>
            <div className="app-modal-header">
              <div className="app-modal-header-text">
                <h2>{editing ? 'Edit deal' : 'New deal'}</h2>
              </div>
              <button type="button" className="app-modal-close" onClick={closeForm} aria-label="Close">
                ×
              </button>
            </div>
            <div className="modal-form">
              <div className="form-group">
                <label>Company</label>
                <Dropdown
                  value={form.company}
                  options={formCompanyOptions}
                  onChange={(e) => {
                    const nextCompany = e.value;
                    setForm({
                      ...form,
                      company: nextCompany,
                      overageRate: form.support ? rateFromCompany(nextCompany) : form.overageRate
                    });
                  }}
                  placeholder="Select company"
                  className="w-full"
                  filter
                  appendTo={document.body}
                  panelClassName="deals-dropdown-panel"
                />
              </div>
              <div className="form-group">
                <label>Name</label>
                <InputText
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. WordPress maintenance, 10h retainer"
                />
              </div>
              <div className="form-group">
                <label>What is included</label>
                <InputTextarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={3}
                  autoResize
                  placeholder="Hosting, updates, agreed hours, specific systems…"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Type</label>
                  <Dropdown
                    value={form.kind}
                    options={[
                      { label: 'Monthly recurring', value: 'RECURRING' },
                      { label: 'One-time', value: 'ONE_TIME' }
                    ]}
                    onChange={(e) => setForm({ ...form, kind: e.value })}
                    className="w-full"
                    appendTo={document.body}
                    panelClassName="deals-dropdown-panel"
                  />
                </div>
                {editing && (
                  <div className="form-group">
                    <label>Status</label>
                    <Dropdown
                      value={form.status}
                      options={[
                        { label: 'Active', value: 'ACTIVE' },
                        { label: 'Paused', value: 'PAUSED' },
                        { label: 'Ended', value: 'ENDED' }
                      ]}
                      onChange={(e) => setForm({ ...form, status: e.value })}
                      className="w-full"
                      appendTo={document.body}
                      panelClassName="deals-dropdown-panel"
                    />
                  </div>
                )}
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>{form.support ? 'Hours in package' : 'Qty / hours'}</label>
                  <InputNumber
                    value={form.quantity}
                    onValueChange={(e) => setForm({ ...form, quantity: e.value || 1 })}
                    min={0.01}
                    minFractionDigits={0}
                    maxFractionDigits={2}
                  />
                </div>
                <div className="form-group">
                  <label>Unit price</label>
                  <InputNumber
                    value={form.unitPrice}
                    onValueChange={(e) => setForm({ ...form, unitPrice: e.value || 0 })}
                    mode="currency"
                    currency="USD"
                    locale="en-US"
                    min={0}
                  />
                </div>
              </div>
              <div className="deal-auto-toggle">
                <div className="deal-auto-toggle-row">
                  <strong>Support</strong>
                  <InputSwitch
                    checked={form.support}
                    onChange={(e) => {
                      const support = !!e.value;
                      setForm({
                        ...form,
                        support,
                        overageRate: support ? (form.overageRate || companyRate) : form.overageRate
                      });
                    }}
                  />
                </div>
                <small>
                  Ticket hours for this company count against the package. Time over the package is billed at the overage rate.
                </small>
              </div>
              {form.support && (
                <div className="form-group">
                  <label>Overage hourly rate</label>
                  <InputNumber
                    value={form.overageRate}
                    onValueChange={(e) => setForm({ ...form, overageRate: e.value || 0 })}
                    mode="currency"
                    currency="USD"
                    locale="en-US"
                    min={0}
                  />
                  <p className="deal-field-hint">
                    Charged after the package hours are used.
                    {companyRate > 0
                      ? ` Company rate is ${formatMoney(companyRate)}/h.`
                      : ' Set a company hourly rate if you want that as the default.'}
                  </p>
                </div>
              )}
              <div className="form-group">
                <label>Client signature</label>
                {form.clientSignature && (
                  <img
                    src={form.clientSignature.startsWith('data:') ? form.clientSignature : getAssetUrl(form.clientSignature)}
                    alt="Client signature"
                    className="deal-signature-preview"
                  />
                )}
                <div className="deal-signature-actions">
                  <label className="deal-file-btn">
                    {form.clientSignature ? 'Replace signature' : 'Upload signature'}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      hidden
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        e.target.value = '';
                        if (!file) return;
                        const reader = new FileReader();
                        reader.onload = () => {
                          setForm((prev) => ({ ...prev, clientSignature: String(reader.result || '') }));
                        };
                        reader.readAsDataURL(file);
                      }}
                    />
                  </label>
                  {form.clientSignature && (
                    <button
                      type="button"
                      className="text-btn"
                      onClick={() => setForm({ ...form, clientSignature: '' })}
                    >
                      Remove
                    </button>
                  )}
                </div>
                <p className="deal-field-hint">Shown on the deal PDF next to the Uptimio signature. PNG or JPG.</p>
              </div>
              <div className="deal-auto-toggle">
                <div className="deal-auto-toggle-row">
                  <strong>Add on From hours invoices</strong>
                  <InputSwitch
                    checked={form.autoInvoice}
                    onChange={(e) => setForm({ ...form, autoInvoice: !!e.value })}
                  />
                </div>
                <small>
                  {form.kind === 'ONE_TIME'
                    ? 'Included once on the next automatic invoice, then skipped.'
                    : 'Included automatically each month until you pause or end the deal.'}
                </small>
              </div>
              <div className="modal-actions">
                <button type="button" className="text-btn" onClick={closeForm}>Cancel</button>
                <Button
                  label={saving ? 'Saving…' : (editing ? 'Save deal' : 'Create deal')}
                  onClick={saveDeal}
                  disabled={saving || !form.name.trim() || !form.company}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Deals;
